/**
 * Acquisition marketing spend, sourced from the Casino Ad Service.
 *
 * The Performance Overview's three ad-spend KPIs (Budget spent, Blended CPA, ROI)
 * have no stats field; their money source is the ad-service campaign performance
 * `cost`. This hook resolves the tenant's total spend over the FOUR windows the
 * performance grid compares (left current + baseline, right current + baseline):
 * it lists the tenant's campaigns once, then sums each campaign's `summary.cost`
 * for each window. The figures feed {@link buildAdSpendStat}.
 *
 * Cost is `null` until the first resolve (the KPIs keep their `?` look meanwhile);
 * `errored` flips the KPIs to `N/A`. Reads $0 in the test env until a campaign has
 * delivered spend. The list is capped at 100 campaigns (the API page max); a tenant
 * past that would undercount, which is fine pre-proxy (test tenants have one).
 *
 * Contract: `docs/api.owners-hub.acquisition-campaigns.md`.
 */
import React from 'react';

import { getAdCampaignPerformance, listAdCampaigns } from './api/casino-ad-service';

/** An inclusive `date_from`..`date_to` window (YYYY-MM-DD) for the spend query. */
export type AdSpendWindow = { dateFrom: string; dateTo: string };

/** The four windows the performance grid compares. */
export type AdSpendWindows = {
  leftCurrent: AdSpendWindow;
  leftBaseline: AdSpendWindow;
  rightCurrent: AdSpendWindow;
  rightBaseline: AdSpendWindow;
};

export type AdSpendColumn = { cost: number | null; baselineCost: number | null };

export type AdSpendResult = {
  left: AdSpendColumn;
  right: AdSpendColumn;
  /** True until the spend first resolves (or while no tenant is resolved). */
  loading: boolean;
  /** True when the spend request failed (the KPIs then read `N/A`). */
  errored: boolean;
};

const EMPTY: AdSpendColumn = { cost: null, baselineCost: null };
const LOADING: AdSpendResult = { left: EMPTY, right: EMPTY, loading: true, errored: false };

/**
 * @param tenantId The active brand's tenant id (`Brand.tenant_id`); `undefined`
 *        keeps the hook loading until a brand resolves.
 * @param windows The four spend windows (inclusive dates), aligned with the
 *        performance grid's stats windows.
 */
export const useAcquisitionAdSpend = (tenantId: string | undefined, windows: AdSpendWindows): AdSpendResult => {
  const [result, setResult] = React.useState<AdSpendResult>(LOADING);

  const { leftCurrent, leftBaseline, rightCurrent, rightBaseline } = windows;

  React.useEffect(() => {
    if (!tenantId) {
      setResult(LOADING);
      return;
    }
    const controller = new AbortController();
    const { signal } = controller;
    setResult(LOADING);

    const run = async (): Promise<void> => {
      const list = await listAdCampaigns({ tenantId, pageSize: 100 }, signal);
      const ids = list.campaigns.map((campaign) => campaign.campaign_id);

      // Total spend over one window = sum of every campaign's summary cost for it.
      const costOver = async (window: AdSpendWindow): Promise<number> => {
        if (ids.length === 0) return 0;
        const costs = await Promise.all(
          ids.map((id) =>
            getAdCampaignPerformance(
              id,
              tenantId,
              { breakdown: 'summary', dateFrom: window.dateFrom, dateTo: window.dateTo },
              signal,
            )
              .then((perf) => perf.totals.cost ?? 0)
              // One campaign with no analytics (404/5xx, e.g. paused or just-created)
              // counts as zero spend rather than collapsing every KPI to N/A. Mirrors
              // useAcquisitionCampaigns' per-item tolerance. A genuine total failure
              // still surfaces: the list fetch above is uncaught, and an abort is caught
              // here but discarded by the `signal.aborted` guard before any setResult.
              .catch(() => 0),
          ),
        );
        return costs.reduce((sum, cost) => sum + cost, 0);
      };

      const [lc, lb, rc, rb] = await Promise.all([
        costOver(leftCurrent),
        costOver(leftBaseline),
        costOver(rightCurrent),
        costOver(rightBaseline),
      ]);

      if (signal.aborted) return;
      setResult({
        left: { cost: lc, baselineCost: lb },
        right: { cost: rc, baselineCost: rb },
        loading: false,
        errored: false,
      });
    };

    run().catch(() => {
      // Ignore the abort the cleanup triggers; a real failure surfaces as `N/A`.
      if (!signal.aborted) setResult({ left: EMPTY, right: EMPTY, loading: false, errored: true });
    });

    return () => controller.abort();
    // The window objects are memoised by the caller, so their identity is stable until
    // a date actually changes (a range switch or the live tick rolling the window).
  }, [tenantId, leftCurrent, leftBaseline, rightCurrent, rightBaseline]);

  return result;
};

export default useAcquisitionAdSpend;
